import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { api, assetUrl } from '../lib/api';
// No mock data imports — all data loaded from API
import { Stars, Badge, Btn, InstructorCard, SectionHeading, AsanohaBackground, languageMeta } from '../components';
import { Avatar } from '../components/Avatar';
import { TimezoneSelect } from '../components/TimezoneSelect';
import { LazyPanel } from '../components/LazyPanel';
import { RequestTimeButton } from '../components/RequestTimeButton';
import { NotifyMeButton } from '../components/NotifyMeButton';
import { formatInTz } from '../lib/timezone';
import { useViewerTz } from '../hooks/useViewerTz';
import { usePayApplies } from '../hooks/usePayApplies';
import { PAY_THROUGH_ROPIDA_CARD } from '../lib/payments';
import { isSafeLinkUrl, parseIntroVideo } from '../lib/urls';
import { sessionMatches, sessionLevelLabel, WEEKDAYS, TIME_OF_DAY, SESSION_LEVEL } from '../lib/sessionTime';
import { profileTabFromHash, PROFILE_TABS } from '../lib/rebook';
import { SPECIALTIES, KIDS_SPECIALTY, isListedSpecialty } from '../lib/specialties';
import { palette as T, alpha, type } from '../design';

// ropida/marketplace.jsx — Marketplace + Instructor Profile

// FullCalendar is heavy — only load it when a visitor actually opens the Sessions tab.
const SessionCalendar = React.lazy(() =>
  import('../components/SessionCalendar').then((m) => ({ default: m.SessionCalendar }))
);


// ── Marketplace ───────────────────────────────────────────────────────────────

// Price comes from `minSessionPrice` — the cheapest seat a student can actually book — not the
// instructor-typed profile rate, which drifted from what sessions really cost (TTT-64).
// An instructor with nothing upcoming has no price: they stay in the unfiltered roster, but a
// price cap hides them (no price can satisfy it) and they sort last in either direction.
// Day and time-of-day filters (FB-3) keep an instructor only when one of their upcoming sessions
// has a seat left and falls on a chosen day and part of the day in the student's timezone `tz`.
// The level filter (FB-4) works the same way: the same session must also fit a chosen level, and a
// session with no level fits every level.
// Pure and exported so these rules are testable without rendering the page.
export function filterAndSortInstructors(instructors, opts = {}) {
  const {
    languages = [], specialties = [], maxPrice = null,
    minRating = 0, childrenOnly = false, search = '', sort = 'recommended',
    days = [], timesOfDay = [], levels = [], tz,
  } = opts;
  const term = search.trim().toLowerCase();
  const filterBySession = days.length > 0 || timesOfDay.length > 0 || levels.length > 0;
  return instructors.filter(ins => {
    if (filterBySession
      && !(ins.upcomingSessions || []).some(s => sessionMatches(s, { days, timesOfDay, levels }, tz))) return false;
    if (languages.length && !languages.includes(ins.language)) return false;
    // Only listed specialties match (IW-3): a typed one stays on the profile but meets no filter.
    if (specialties.length
      && !specialties.some(s => isListedSpecialty(s) && ins.specialties?.includes(s))) return false;
    // Explicit type check, not `!(price <= maxPrice)` — `null <= 1000` is true in JS, so a
    // priceless instructor would sail through every cap.
    if (maxPrice !== null
      && (typeof ins.minSessionPrice !== 'number' || ins.minSessionPrice > maxPrice)) return false;
    if (minRating && ins.rating < minRating) return false;
    if (childrenOnly && !ins.specialties?.includes(KIDS_SPECIALTY)) return false;
    // A student types the language name ("Japanese"), not our code ("JP"); match both (KP-6.5).
    if (term && !ins.displayName.toLowerCase().includes(term) &&
        !ins.specialties?.join(' ').toLowerCase().includes(term) &&
        !(ins.language && languageMeta(ins.language).label.toLowerCase().includes(term)) &&
        ins.language?.toLowerCase() !== term) return false;
    return true;
  }).sort((a, b) => {
    if (sort === 'price-asc') return (a.minSessionPrice ?? Infinity) - (b.minSessionPrice ?? Infinity);
    if (sort === 'price-desc') return (b.minSessionPrice ?? -Infinity) - (a.minSessionPrice ?? -Infinity);
    if (sort === 'rating') return b.rating - a.rating;
    if (sort === 'reviews') return b.reviewCount - a.reviewCount;
    return 0;
  });
}

// Language codes with at least one listed instructor, in roster order. The home page and the
// language filter offer only these, so no student is promised a language nobody teaches (FB-5).
export function taughtLanguages(instructors) {
  return Array.from(new Set((instructors || []).map(i => i.language).filter(Boolean)));
}

// Below this many listed instructors the filter panel folds behind a Filters button: a full panel
// beside two instructors offers choices that lead nowhere (FB-5).
export const FOLD_FILTERS_BELOW = 4;

// Chip labels for the day and time-of-day filters (FB-3). Hours are the viewer's own timezone.
const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const TIME_OF_DAY_LABELS = {
  [TIME_OF_DAY.MORNING]: 'Morning (before 12:00)',
  [TIME_OF_DAY.AFTERNOON]: 'Afternoon (12:00–17:00)',
  [TIME_OF_DAY.EVENING]: 'Evening (from 17:00)',
};

function MarketplacePage({ setPage }) {
  const isMobile = useIsMobile();
  const [viewerTz, setViewerTz] = useViewerTz();
  const [filterDays, setFilterDays] = React.useState([]);
  const [filterTimes, setFilterTimes] = React.useState([]);
  const [filterLevels, setFilterLevels] = React.useState([]);
  const [filterLang, setFilterLang] = React.useState([]);
  const [filterSpecialties, setFilterSpecialties] = React.useState([]);
  // null = no cap. The cap used to default to a hardcoded ₱2,000 *and* the slider maxed out
  // there, so an instructor who priced above it vanished from the marketplace with no slider
  // position that could bring them back (TTT-55). Both ends now come from the live roster.
  const [filterMaxPrice, setFilterMaxPrice] = React.useState(null);
  const [filterRating, setFilterRating] = React.useState(0);
  const [filterChildren, setFilterChildren] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const [sort, setSort] = React.useState('recommended');
  const [showFilters, setShowFilters] = React.useState(false);

  const [allInstructors, setAllInstructors] = React.useState([]);
  const [listLoading, setListLoading] = React.useState(true);

  React.useEffect(() => {
    setListLoading(true);
    api.get('/instructors').then(data => {
      if (data?.instructors) setAllInstructors(data.instructors);
    }).catch(() => {}).finally(() => setListLoading(false));
  }, []);

  const toggle = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter(x=>x!==val) : [...arr, val]);

  // Price slider bounds follow the live roster's real session prices, so nobody can be priced
  // off the scale.
  const prices = allInstructors.map(i => i.minSessionPrice).filter(p => typeof p === 'number' && p > 0);
  const priceFloor = prices.length ? Math.floor(Math.min(...prices) / 100) * 100 : 0;
  const priceCeiling = Math.max(
    prices.length ? Math.ceil(Math.max(...prices) / 100) * 100 : 0,
    priceFloor + 100
  );

  // Phones always fold the panel; a short roster folds it at every width.
  const foldFilters = isMobile || allInstructors.length < FOLD_FILTERS_BELOW;

  const activeFilterCount = filterDays.length + filterTimes.length + filterLevels.length
    + filterLang.length + filterSpecialties.length
    + (filterMaxPrice !== null ? 1 : 0) + (filterRating ? 1 : 0) + (filterChildren ? 1 : 0);

  // One reset for "Reset all" and the empty-result button, so neither can leave a filter behind.
  const clearFilters = () => {
    setFilterDays([]); setFilterTimes([]); setFilterLevels([]);
    setFilterLang([]); setFilterSpecialties([]); setFilterMaxPrice(null); setFilterRating(0); setFilterChildren(false);
  };
  const clearFiltersAndSearch = () => { clearFilters(); setSearchText(''); };

  const filteredInstructors = filterAndSortInstructors(allInstructors, {
    days: filterDays,
    timesOfDay: filterTimes,
    levels: filterLevels,
    tz: viewerTz,
    languages: filterLang,
    specialties: filterSpecialties,
    maxPrice: filterMaxPrice,
    minRating: filterRating,
    childrenOnly: filterChildren,
    search: searchText,
    sort,
  });

  const ChipToggle = ({ label, arr, setArr, value }) => {
    const active = arr.includes(value);
    return (
      <button aria-pressed={active} onClick={() => toggle(arr, setArr, value)} style={{ padding:'5px 12px', borderRadius:9999, border:'1px solid', borderColor: active ? T.terracotta : T.border, background: active ? alpha('terracotta', 'hover') : 'transparent', color: active ? T.terracottaDeep : T.walnut, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, fontWeight: active ? 600 : 400, cursor:'pointer', transition:'all 0.15s' }}>
        {label}
      </button>
    );
  };

  const FilterSection = ({ title, children }) => (
    <div style={{ marginBottom:24, paddingBottom:24, borderBottom:'1px solid ' + T.border }}>
      <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.label, color:T.espresso, textTransform:'uppercase', letterSpacing:0.8, margin:'0 0 12px' }}>{title}</p>
      {children}
    </div>
  );

  return (
    <div style={{ background: T.ivory, paddingTop: isMobile ? 72 : 80, minHeight:'100vh' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding: isMobile ? '16px' : '32px', display:'grid', gridTemplateColumns: foldFilters ? '1fr' : '260px 1fr', gap: foldFilters ? 16 : 32, alignItems:'start' }}>

        {/* Filter toggle: on phones, and at every width while the roster is short */}
        {foldFilters && (
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <button aria-expanded={showFilters} onClick={() => setShowFilters(!showFilters)} style={{ padding:'10px 18px', borderRadius:12, border:'1px solid ' + T.border, background: showFilters ? alpha('terracotta', 'hover') : T.white, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, fontWeight:600, color:T.terracotta, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
            </button>
            {isMobile && <select value={sort} onChange={e=>setSort(e.target.value)}
              style={{ flex:1, padding:'10px 14px', borderRadius:12, border:'1px solid ' + T.border, background:T.white, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.espresso, cursor:'pointer', outline:'none' }}>
              <option value="recommended">Recommended</option>
              <option value="rating">Highest rated</option>
              <option value="reviews">Most reviews</option>
              <option value="price-asc">Lowest price</option>
              <option value="price-desc">Highest price</option>
            </select>}
          </div>
        )}

        {/* ── Sidebar ── */}
        {(!foldFilters || showFilters) && <div style={{ position: foldFilters ? 'static' : 'sticky', top:88, background:T.white, borderRadius:16, border:'1px solid ' + T.border, padding:24, boxShadow:'0 2px 12px ' + alpha('espresso', 'shadow') }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <p style={{ fontFamily:"'Lora', serif", fontWeight:700, fontSize:type.h4, color:T.espresso, margin:0 }}>Filters</p>
            <button onClick={clearFilters}
              style={{ background:'none', border:'none', cursor:'pointer', fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.terracotta }}>Reset all</button>
          </div>

          {/* Day and time of day (FB-3): matched against each instructor's upcoming sessions with a
              seat left, in the student's own timezone. One session must fit both choices. */}
          <FilterSection title="Day">
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {WEEKDAYS.map(d => (
                <ChipToggle key={d} label={DAY_LABELS[d]} arr={filterDays} setArr={setFilterDays} value={d} />
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Time of day">
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
              {Object.values(TIME_OF_DAY).map(t => (
                <ChipToggle key={t} label={TIME_OF_DAY_LABELS[t]} arr={filterTimes} setArr={setFilterTimes} value={t} />
              ))}
            </div>
            <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.driftwood, margin:'0 0 6px' }}>Days and times in your timezone:</p>
            <TimezoneSelect value={viewerTz} onChange={setViewerTz}
              style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.espresso, padding:'6px 8px', borderRadius:8, border:'1px solid ' + T.border, background:T.white, outline:'none', width:'100%' }} />
          </FilterSection>

          <FilterSection title="Language">
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {taughtLanguages(allInstructors).map(code => (
                <ChipToggle key={code} label={`${languageMeta(code).flag} ${languageMeta(code).label}`} arr={filterLang} setArr={setFilterLang} value={code} />
              ))}
            </div>
          </FilterSection>

          {/* Level (FB-4): matched against each session's level, which instructors set per
              session. A session with no level is open to all levels and matches every chip. */}
          <FilterSection title="Level">
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {Object.values(SESSION_LEVEL).map(l => (
                <ChipToggle key={l} label={sessionLevelLabel(l)} arr={filterLevels} setArr={setFilterLevels} value={l} />
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Specialties">
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {SPECIALTIES.map(s => (
                <ChipToggle key={s} label={s} arr={filterSpecialties} setArr={setFilterSpecialties} value={s} />
              ))}
            </div>
          </FilterSection>

          {/* "Starting rate" named the instructor's profile field, not what a student pays. The
              slider filters on the cheapest session they can book (TTT-64), so say that. */}
          <FilterSection title="Max session price">
            <div style={{ padding:'0 4px' }}>
              <input type="range" min={priceFloor} max={priceCeiling} step={100} value={filterMaxPrice ?? priceCeiling}
                onChange={e => setFilterMaxPrice(+e.target.value >= priceCeiling ? null : +e.target.value)}
                style={{ width:'100%', accentColor:T.terracotta }} />
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.driftwood }}>₱{priceFloor.toLocaleString()}</span>
                <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, fontWeight:600, color:T.espresso }}>{filterMaxPrice === null ? 'Any' : `₱${filterMaxPrice.toLocaleString()}`}</span>
                <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.driftwood }}>₱{priceCeiling.toLocaleString()}</span>
              </div>
            </div>
          </FilterSection>

          <FilterSection title="Minimum rating">
            <div style={{ display:'flex', gap:8 }}>
              {[4.5, 4.8].map(r => (
                <button key={r} onClick={() => setFilterRating(filterRating === r ? 0 : r)} style={{ padding:'5px 12px', borderRadius:9999, border:'1px solid', borderColor: filterRating === r ? T.terracotta : T.border, background: filterRating === r ? alpha('terracotta', 'hover') : 'transparent', color: filterRating === r ? T.terracottaDeep : T.walnut, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, cursor:'pointer' }}>
                  {r}+
                </button>
              ))}
            </div>
          </FilterSection>

          <div style={{ marginBottom:0 }}>
            <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.label, color:T.espresso, textTransform:'uppercase', letterSpacing:0.8, margin:'0 0 12px' }}>Teaches children</p>
            {/* A real switch: the keyboard reaches it, Enter or Space flips it, and a screen
                reader hears its name and whether it is on (DS-4). */}
            <button type="button" role="switch" aria-checked={filterChildren} onClick={() => setFilterChildren(!filterChildren)}
              style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', background:'none', border:'none', padding:0 }}>
              <span aria-hidden="true" style={{ display:'block', flexShrink:0, width:42, height:24, borderRadius:12, background: filterChildren ? T.terracotta : T.border, transition:'background 0.2s', position:'relative' }}>
                <span style={{ position:'absolute', top:3, left: filterChildren ? 20 : 3, width:18, height:18, borderRadius:'50%', background:T.white, transition:'left 0.2s', boxShadow:'0 1px 4px ' + alpha('espresso', 'shadow') }} />
              </span>
              <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut }}>Kid-friendly only</span>
            </button>
          </div>
        </div>}

        {/* ── Main ── */}
        <div>
          {/* Search + sort bar */}
          <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
            <div style={{ flex:1, minWidth:200, position:'relative' }}>
              <span style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', fontSize:type.lead, color:T.driftwood }}>⌕</span>
              <input value={searchText} onChange={e=>setSearchText(e.target.value)} placeholder="Name, specialty, language…"
                style={{ width:'100%', padding:'11px 14px 11px 40px', borderRadius:12, border:'1px solid ' + T.border, background:T.white, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.espresso, outline:'none', boxSizing:'border-box' }}
                onFocus={e=>e.target.style.borderColor=T.terracotta} onBlur={e=>e.target.style.borderColor=T.border} />
            </div>
            {!isMobile && <select value={sort} onChange={e=>setSort(e.target.value)}
              style={{ padding:'11px 16px', borderRadius:12, border:'1px solid ' + T.border, background:T.white, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.espresso, cursor:'pointer', outline:'none' }}>
              <option value="recommended">Recommended</option>
              <option value="rating">Highest rated</option>
              <option value="reviews">Most reviews</option>
              <option value="price-asc">Lowest price</option>
              <option value="price-desc">Highest price</option>
            </select>}
          </div>

          {/* Results header */}
          <div style={{ marginBottom:20 }}>
            <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut, margin:0 }}>
              {listLoading ? 'Loading…' : (
                <>Showing <strong style={{ color:T.espresso }}>{filteredInstructors.length}</strong> instructor{filteredInstructors.length !== 1 ? 's' : ''}</>
              )}
            </p>
          </div>

          {/* Grid */}
          {listLoading ? (
            <div style={{ textAlign:'center', padding:'80px 32px' }}>
              <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.driftwood }}>Loading…</p>
            </div>
          ) : filteredInstructors.length === 0 ? (
            <div style={{ textAlign:'center', padding:'80px 32px' }}>
              <h3 style={{ fontFamily:"'Lora', serif", fontWeight:700, fontSize:24, color:T.espresso, margin:'0 0 8px' }}>No instructors found</h3>
              {/* FB-3.3: a filtered dead end always offers the way back. */}
              {activeFilterCount > 0 || searchText.trim() ? (
                <>
                  <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut, margin:'0 0 16px' }}>No instructor matches what you chose. Clear the filters to see every instructor.</p>
                  <button onClick={clearFiltersAndSearch}
                    style={{ padding:'10px 20px', borderRadius:12, border:'none', background:T.terracotta, color:T.white, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, fontWeight:600, cursor:'pointer' }}>
                    Clear filters
                  </button>
                </>
              ) : (
                <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut }}>No instructors are listed yet. Please check back soon.</p>
              )}
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:24 }}>
              {filteredInstructors.map(ins => (
                <InstructorCard key={ins.id} instructor={ins} onClick={() => setPage('instructor-profile-' + (ins.slug || ins.id))} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Instructor Profile ────────────────────────────────────────────────────────

// The profile's language line (FS-5): every language the instructor lists on their dashboard, in
// their order — the same list and order the link preview reads (backend describeInstructor, #351).
// An instructor who lists none still shows their main language, as the profile always did.
export function teachesLine(instructor) {
  const listed = (instructor.teaches || []).map(l => l.trim()).filter(Boolean);
  return `Teaches ${listed.length ? listed.join(', ') : languageMeta(instructor.language).label}`;
}

// The instructor's introduction, at the top of their profile — a student decides whether to book
// from a face and a voice long before they finish the bio (TTT-13).
//
// Click to play: nothing is requested from YouTube or Loom until the student asks for it, so a
// profile view doesn't cost ~1MB and a third-party cookie for a video most visitors won't watch.
// The `autoplay=1` on the embed is that click being honoured — without it they'd press play twice.
function IntroVideo({ videoUrl, displayName, isMobile }) {
  const [playing, setPlaying] = React.useState(false);
  // A poster is a request to someone else's CDN and can 404 (or be blocked). Falling back to the
  // branded panel keeps a play button the student can use instead of a broken image icon.
  const [posterFailed, setPosterFailed] = React.useState(false);
  const video = parseIntroVideo(videoUrl);

  if (!videoUrl) return null;

  const frame = { width: '100%', maxWidth: 480, aspectRatio: '16/9', borderRadius: 12, border: '1px solid ' + T.border };
  const showPoster = Boolean(video?.posterUrl) && !posterFailed;

  // A link we recognise nothing about still deserves a way through — but only if handing it to the
  // browser is safe. An unopenable scheme gets no link at all; the instructor is told at save time
  // rather than the student being shown something we won't let them click.
  if (!video) {
    if (!isSafeLinkUrl(videoUrl)) return null;
    return (
      <div style={{ marginTop: 20 }}>
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0 }}>
          {displayName} added an intro video we can't play here.{' '}
          <a href={videoUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.terracotta }}>Open it in a new tab →</a>
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20 }}>
      <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.espresso, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 10px' }}>
        Meet {displayName}
      </h2>
      {playing ? (
        <iframe
          src={video.embedUrl}
          title={`Intro video from ${displayName}`}
          style={{ ...frame, display: 'block' }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button
          onClick={() => setPlaying(true)}
          aria-label={`Play intro video from ${displayName}`}
          style={{ ...frame, position: 'relative', padding: 0, overflow: 'hidden', cursor: 'pointer', display: 'block', background: T.warmGrey }}
        >
          {showPoster && (
            <img
              src={video.posterUrl}
              alt=""
              loading="lazy"
              onError={() => setPosterFailed(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
          <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: showPoster ? alpha('espresso', 'selection') : 'transparent' }}>
            <span style={{ background: T.terracotta, border: '3px solid ' + T.white, borderRadius: '50%', width: isMobile ? 52 : 60, height: isMobile ? 52 : 60, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px ' + alpha('terracotta', 'shadow'), color: T.white, fontSize: type.h3, paddingLeft: 4 }}>▶</span>
          </span>
        </button>
      )}
    </div>
  );
}

function InstructorProfilePage({ instructorId, setPage, openBooking }) {
  const isMobile = useIsMobile();
  const [instructor, setInstructor] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  // `#/instructor-profile-<slug>?tab=sessions` (from "Book again") opens straight on that tab.
  const [tab, setTab] = React.useState(() => profileTabFromHash(window.location.hash));
  const [reviewSort, setReviewSort] = React.useState('helpful');
  // JTP-29: a student Pay applies to reads that they pay through Ropida.
  const paysThroughRopida = usePayApplies();

  React.useEffect(() => {
    setLoading(true);
    // An empty id (a truncated shared link, `#/instructor-profile-`) would request
    // `/instructors/` — the LIST endpoint, which answers 200 with `{ instructors: [...] }`.
    // `data.instructor || data` would then store that list as if it were an instructor and
    // render a broken profile instead of "Instructor not found".
    if (!instructorId) {
      setInstructor(null);
      setLoading(false);
      return;
    }
    api.get(`/instructors/${encodeURIComponent(instructorId)}`)
      .then(data => {
        setInstructor(data.instructor || null);
      })
      .catch(() => {
        setInstructor(null);
      })
      .finally(() => setLoading(false));
  }, [instructorId]);

  const reviews = instructor?.reviews || [];
  const sortedReviews = [...reviews].sort((a,b) => {
    if (reviewSort === 'helpful') return b.helpful - a.helpful;
    if (reviewSort === 'newest') return new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date);
    if (reviewSort === 'highest') return b.rating - a.rating;
    return a.rating - b.rating;
  });

  // Upcoming bookable sessions, shown in the viewer's own timezone. Students can select
  // several (calendar blocks or list cards) and book them together.
  const [sessions, setSessions] = React.useState([]);
  const [viewerTz, setViewerTz] = useViewerTz();
  const [sessionsView, setSessionsView] = React.useState('calendar'); // 'calendar' | 'list'
  const [selectedIds, setSelectedIds] = React.useState([]);
  // `instructorId` may be a slug (this page reached via its shareable URL, TTT-86) — the route
  // resolves either, same as GET /instructors/:id, so this fires in parallel with the instructor
  // fetch above rather than waiting on it.
  React.useEffect(() => {
    api.get(`/sessions/instructor/${instructorId}`)
      .then(data => setSessions(data?.sessions || []))
      .catch(() => setSessions([]));
  }, [instructorId]);

  const toggleSession = (s) => {
    if (s.seatsLeft === 0) return; // full sessions aren't selectable
    setSelectedIds(ids => ids.includes(s.id) ? ids.filter(id => id !== s.id) : [...ids, s.id]);
  };
  const selectedSessions = sessions.filter(s => selectedIds.includes(s.id));
  // No upcoming session with a seat left: the sidebar offers "Request a time" instead (FB-1).
  const hasOpenSeat = sessions.some(s => s.seatsLeft > 0);

  if (loading) {
    return (
      <div style={{ background: T.ivory, paddingTop: isMobile ? 72 : 80, minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.driftwood }}>Loading instructor…</p>
      </div>
    );
  }

  if (!instructor) {
    return (
      <div style={{ background: T.ivory, paddingTop: isMobile ? 72 : 80, minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.driftwood }}>Instructor not found.</p>
      </div>
    );
  }

  return (
    <div style={{ background: T.ivory, paddingTop: isMobile ? 72 : 80, minHeight:'100vh' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding: isMobile ? '16px' : '40px 32px' }}>
        {/* Back button */}
        <button onClick={() => setPage('marketplace')} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.driftwood, display:'flex', alignItems:'center', gap:6, marginBottom:24, padding:0 }}>
          ← Back to all instructors
        </button>

        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: isMobile ? 20 : 40, alignItems:'start' }}>
          {/* ── Left column ── */}
          <div>
            {/* Header */}
            <div style={{ background:T.white, borderRadius:16, border:'1px solid ' + T.border, padding: isMobile ? 20 : 32, marginBottom:24, display:'flex', gap: isMobile ? 16 : 28, alignItems:'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
              {/* Photo */}
              <div style={{ position:'relative', flexShrink:0 }}>
                <div style={{ width:140, height:170, borderRadius:16, overflow:'hidden', position:'relative', background:T.warmGrey }}>
                  {instructor.photo ? (
                    <img src={assetUrl(instructor.photo)} alt={instructor.displayName} style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'top' }} />
                  ) : (
                    <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><Avatar name={instructor.displayName} size={96} /></div>
                  )}
                </div>
                {/* The intro video used to hide behind a 42px circle on this photo that opened a
                    modal. It now plays in the header itself (TTT-13) — see IntroVideo below. */}
              </div>
              {/* Info */}
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                  <h1 style={{ fontFamily:"'Lora', serif", fontWeight:700, fontSize:type.h2, color:T.espresso, margin:0 }}>{instructor.displayName}</h1>
                  <span style={{ fontSize:type.h3 }}>{instructor.flag}</span>
                  {instructor.verified && <Badge variant="sage">✓ Verified</Badge>}
                  {/* Same guard as the video link below: these URLs are instructor-typed and land in
                      an href, so an unopenable scheme (javascript:) must never reach the browser. */}
                  {instructor.socialLinks && Object.entries(instructor.socialLinks).filter(([, v]) => isSafeLinkUrl(v)).map(([k, url]) => (
                    <a key={k} href={url} target="_blank" rel="noopener noreferrer" style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.caption, color:T.terracotta, textDecoration:'none', background:T.ivory, border:'1px solid ' + T.border, borderRadius:9999, padding:'3px 10px' }}>{k}</a>
                  ))}
                </div>
                {/* nameJP/nameZH, responseTime, resume, conversational, levels and interests are
                    not rendered: no dashboard input can set them, so an instructor could never
                    correct what students saw here (TTT-55). Re-add each one with its input. */}
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <Stars rating={instructor.rating} size={14} />
                  <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:600, fontSize:type.body, color:T.espresso }}>{instructor.rating}</span>
                  <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut }}>({instructor.reviewCount} reviews)</span>
                </div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
                  <Badge variant={languageMeta(instructor.language).variant}>{teachesLine(instructor)}</Badge>
                  {(instructor.specialties || []).slice(0,3).map(s => <Badge key={s} variant="subtle">{s}</Badge>)}
                </div>
                {instructor.tagline && <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut, margin:'0 0 12px', fontStyle:'italic' }}>"{instructor.tagline}"</p>}
                <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
                  {instructor.country && <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut }}>{instructor.country}</span>}
                  {/* Student/hours totals intentionally not shown: unfair to new instructors vs. established
                      ones, and currently self-reported. Revisit once computed from real bookings. */}
                  {/* The instructor's email is deliberately not shown here — this page is public and
                      anonymous, so publishing it makes addresses scrapeable. Students who book get it
                      in the booking confirmation and on their dashboard. */}
                </div>
                {/* Below the name and rating rather than beside the photo: on a phone this stacks
                    with who the instructor is still on screen above the video. */}
                <IntroVideo videoUrl={instructor.videoUrl} displayName={instructor.displayName} isMobile={isMobile} />
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display:'flex', gap:4, marginBottom:20, background:T.white, borderRadius:14, padding:6, border:'1px solid ' + T.border, width: isMobile ? '100%' : 'fit-content' }}>
              {PROFILE_TABS.map(t => (
                <button key={t} onClick={() => setTab(t)} style={{ padding: isMobile ? '8px 10px' : '8px 18px', borderRadius:10, border:'none', background: tab === t ? T.terracotta : 'transparent', color: tab === t ? T.white : T.walnut, fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:600, fontSize:type.body, cursor:'pointer', textTransform:'capitalize', transition:'all 0.15s', flex: isMobile ? 1 : 'none' }}>{t}</button>
              ))}
            </div>

            {/* Tab: About */}
            {tab === 'about' && (
              <div style={{ background:T.white, borderRadius:16, border:'1px solid ' + T.border, padding:28 }}>
                <h3 style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.lead, color:T.espresso, margin:'0 0 14px' }}>About {instructor.displayName}</h3>
                {(instructor.bio || '').split('\n\n').map((p,i) => (
                  <p key={i} style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut, lineHeight:1.75, marginBottom:14 }}>{p}</p>
                ))}
                <div style={{ borderTop:'1px solid ' + T.border, paddingTop:20, marginTop:8 }}>
                  <h4 style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.body, color:T.espresso, margin:'0 0 10px' }}>Teaching style</h4>
                  <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut, lineHeight:1.7, margin:'0 0 20px' }}>{instructor.teachingStyle}</p>

                  {(instructor.credentials || []).length > 0 && (
                    <>
                      <h4 style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.body, color:T.espresso, margin:'0 0 10px' }}>Credentials</h4>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:20 }}>
                        {instructor.credentials.map(c => (
                          <span key={c} style={{ background:T.ivory, border:'1px solid ' + T.border, borderRadius:9999, padding:'4px 12px', fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.walnut }}>{c}</span>
                        ))}
                      </div>
                    </>
                  )}

                  {(instructor.certifications || []).length > 0 && (
                    <>
                      <h4 style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.body, color:T.espresso, margin:'0 0 10px' }}>Certifications</h4>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:20 }}>
                        {instructor.certifications.map(c => (
                          <span key={c} style={{ background:T.ivory, border:'1px solid ' + T.border, borderRadius:9999, padding:'4px 12px', fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.walnut }}>{c}</span>
                        ))}
                      </div>
                    </>
                  )}

                  {((instructor.native || []).length > 0 || (instructor.fluent || []).length > 0) && (
                    <>
                      <h4 style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.body, color:T.espresso, margin:'0 0 10px' }}>Languages</h4>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:20 }}>
                        {(instructor.native || []).map(l => (
                          <span key={`native-${l}`} style={{ background:T.ivory, border:'1px solid ' + T.border, borderRadius:9999, padding:'4px 12px', fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.walnut }}>{l} — Native</span>
                        ))}
                        {(instructor.fluent || []).map(l => (
                          <span key={`fluent-${l}`} style={{ background:T.ivory, border:'1px solid ' + T.border, borderRadius:9999, padding:'4px 12px', fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.walnut }}>{l} — Fluent</span>
                        ))}
                      </div>
                    </>
                  )}

                  {(instructor.gallery || []).length > 0 && (
                    <>
                      <h4 style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.body, color:T.espresso, margin:'24px 0 10px' }}>Gallery</h4>
                      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap:12 }}>
                        {instructor.gallery.map((url, idx) => (
                          <div key={idx} style={{ borderRadius:12, overflow:'hidden', border:'1px solid ' + T.border, aspectRatio:'4/3' }}>
                            <img src={assetUrl(url)} alt={`Gallery ${idx + 1}`} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Tab: Sessions — the instructor's upcoming scheduled classes, in the viewer's tz.
                Students pick one or more (calendar blocks or list cards) and book them together. */}
            {tab === 'sessions' && (
              <div style={{ background:T.white, borderRadius:16, border:'1px solid ' + T.border, padding: isMobile ? 16 : 28 }}>
                {/* Multi-select booking bar. Sits above the sessions, not below them (TTT-77):
                    students were scrolling past the calendar to find what they had picked. */}
                {selectedSessions.length > 0 && (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:16, padding:'12px 16px', background:T.ivory, border:'1px solid ' + T.terracotta, borderRadius:14, flexWrap:'wrap' }}>
                    <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.espresso }}>
                      <strong>{selectedSessions.length}</strong> session{selectedSessions.length !== 1 ? 's' : ''} selected · ₱{selectedSessions.reduce((sum, s) => sum + (s.price || 0), 0).toLocaleString()}
                    </span>
                    <div style={{ display:'flex', gap:8 }}>
                      <Btn size="sm" variant="ghost" onClick={() => setSelectedIds([])}>Clear</Btn>
                      <Btn size="sm" onClick={() => openBooking(instructor, selectedSessions)}>
                        Book {selectedSessions.length} session{selectedSessions.length !== 1 ? 's' : ''} →
                      </Btn>
                    </div>
                  </div>
                )}

                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                    <h3 style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.lead, color:T.espresso, margin:0 }}>Upcoming sessions</h3>
                    <div style={{ display:'flex', gap:4, background:T.ivory, borderRadius:10, padding:4, border:'1px solid ' + T.border }}>
                      {[['calendar','Calendar'],['list','List']].map(([k,l]) => (
                        <button key={k} onClick={() => setSessionsView(k)}
                          style={{ padding:'5px 12px', borderRadius:7, border:'none', background: sessionsView === k ? T.terracotta : 'transparent', color: sessionsView === k ? T.white : T.walnut, fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:600, fontSize:type.label, cursor:'pointer', transition:'all 0.15s' }}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.driftwood }}>Times in your timezone:</span>
                    <TimezoneSelect value={viewerTz} onChange={setViewerTz}
                      style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.espresso, padding:'6px 8px', borderRadius:8, border:'1px solid ' + T.border, background:T.white, outline:'none', maxWidth:230 }} />
                  </div>
                </div>

                {sessions.length === 0 ? (
                  <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.driftwood, margin:'8px 0' }}>No upcoming sessions right now. Please check back later.</p>
                ) : sessionsView === 'calendar' ? (
                  <LazyPanel fallback={<p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.driftwood }}>Loading calendar…</p>}>
                    <SessionCalendar
                      sessions={sessions}
                      tz={viewerTz}
                      selectedIds={selectedIds}
                      onSessionClick={toggleSession}
                    />
                  </LazyPanel>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    {sessions.map(s => {
                      const full = s.seatsLeft === 0;
                      const isSelected = selectedIds.includes(s.id);
                      const when = formatInTz(s.startAt, viewerTz);
                      // The card is no tab stop (DS-4). A click anywhere on it picks the session,
                      // the same as its "Select {title}" checkbox, which is the named control a
                      // keyboard or screen-reader user reaches. Book names the session it books.
                      return (
                        <div key={s.id} data-testid="session" onClick={() => toggleSession(s)}
                          style={{ borderRadius:14, border: isSelected ? '2px solid ' + T.terracotta : '1px solid ' + (full ? T.border : T.sageGreen), background: full ? T.warmGrey : isSelected ? alpha('terracotta', 'hover') : alpha('sageGreen', 'hover'), padding:'16px 18px', display:'flex', gap:12, alignItems:'flex-start', flexWrap:'wrap', opacity: full ? 0.7 : 1, cursor: full ? 'not-allowed' : 'pointer' }}>
                          <div style={{ flex:1, minWidth:200 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                              <input type="checkbox" aria-label={`Select ${s.title}`} checked={isSelected} disabled={full}
                                onChange={() => toggleSession(s)} onClick={(e) => e.stopPropagation()}
                                style={{ width:18, height:18, margin:0, accentColor:T.terracotta, cursor: full ? 'not-allowed' : 'pointer' }} />
                              <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.body, color:T.espresso, margin:0 }}>{s.title}</p>
                              {/* FB-4.5: every card names its level; no level reads "All levels". */}
                              <Badge variant="subtle">{sessionLevelLabel(s.level)}</Badge>
                              {/* A full session says "Full" alone, never "0 seats left" beside it (KP-6.4). */}
                              {s.capacity > 1 && !full && <Badge variant="gold">Group · {s.seatsLeft} seat{s.seatsLeft !== 1 ? 's' : ''} left</Badge>}
                              {full && <Badge variant="gold">Full</Badge>}
                            </div>
                            <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut, margin:'4px 0 0' }}>{when} · {s.duration} min</p>
                            {s.description && <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.driftwood, margin:'4px 0 0', lineHeight:1.5 }}>{s.description}</p>}
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <p style={{ fontFamily:"'Lora', serif", fontWeight:700, fontSize:type.h4, color:T.espresso, margin:'0 0 8px' }}>₱{(s.price ?? 0).toLocaleString()}</p>
                            <Btn size="sm" disabled={full} aria-label={full ? undefined : `Book → ${s.title}, ${when}`}
                              onClick={full ? undefined : (e) => { e.stopPropagation(); openBooking(instructor, s); }}>{full ? 'Full' : 'Book →'}</Btn>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Reviews */}
            {tab === 'reviews' && (
              <div style={{ background:T.white, borderRadius:16, border:'1px solid ' + T.border, padding:28 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap:8 }}>
                  <div>
                    <div style={{ display:'flex', alignItems:'baseline', gap:10 }}>
                      <span style={{ fontFamily:"'Lora', serif", fontWeight:700, fontSize:type.h1, color:T.espresso }}>{instructor.rating}</span>
                      <Stars rating={instructor.rating} size={18} />
                    </div>
                    <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut, margin:'4px 0 0' }}>{instructor.reviewCount} reviews</p>
                  </div>
                  <select value={reviewSort} onChange={e=>setReviewSort(e.target.value)}
                    style={{ padding:'8px 14px', borderRadius:10, border:'1px solid ' + T.border, background:T.ivory, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, cursor:'pointer' }}>
                    <option value="helpful">Most helpful</option>
                    <option value="newest">Newest</option>
                    <option value="highest">Highest rating</option>
                    <option value="lowest">Lowest rating</option>
                  </select>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  {sortedReviews.map(r => (
                    <div key={r.id} style={{ borderBottom:'1px solid ' + T.border, paddingBottom:16 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:36, height:36, borderRadius:'50%', background:T.warmGrey, display:'flex', alignItems:'center', justifyContent:'center', fontSize:type.body, fontWeight:700, color:T.terracotta, fontFamily:"'Lora', serif" }}>{(r.name || 'S').charAt(0)}</div>
                          <div>
                            <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:600, fontSize:type.body, color:T.espresso, margin:0 }}>{r.name}</p>
                            <Stars rating={r.rating} size={type.caption} />
                          </div>
                        </div>
                        <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.driftwood }}>{r.date}</span>
                      </div>
                      <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.walnut, lineHeight:1.65, margin:0 }}>{r.text}</p>
                      {/* `r.helpful && …` rendered a bare "0" under an unvoted review (KP-6.2). */}
                      {r.helpful > 0 && <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.caption, color:T.driftwood, margin:'8px 0 0' }}>{r.helpful} found this helpful</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Right sidebar ── */}
          <div style={{ position: isMobile ? 'static' : 'sticky', top:88 }}>
            <div style={{ background:T.white, borderRadius:16, border:'1px solid ' + T.border, overflow:'hidden', boxShadow:'0 2px 12px ' + alpha('espresso', 'shadow') }}>
              {/* Header */}
              <div style={{ background:T.terracotta, padding:'20px 24px', color:T.white }}>
                <p style={{ fontFamily:"'Lora', serif", fontWeight:700, fontSize:22, margin:0 }}>Book a session</p>
              </div>
              <div style={{ padding:24 }}>
                {/* Next sessions preview */}
                {/* A full session reads "Full" here too, not a price that suggests a seat (FB-5). */}
                {sessions.slice(0, 3).map(s => {
                  const full = s.seatsLeft === 0;
                  return (
                    <div key={s.id} data-testid="sidebar-session" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, padding:'10px 0', borderBottom:'1px solid ' + T.border, opacity: full ? 0.7 : 1 }}>
                      <div style={{ minWidth:0 }}>
                        <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.espresso, margin:0, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{s.title}</p>
                        <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.caption, color:T.driftwood, margin:0 }}>{formatInTz(s.startAt, viewerTz)}</p>
                      </div>
                      {full
                        ? <Badge variant="gold">Full</Badge>
                        : <span style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:700, fontSize:type.body, color:T.espresso, whiteSpace:'nowrap' }}>₱{(s.price ?? 0).toLocaleString()}</span>}
                    </div>
                  );
                })}
                {sessions.length === 0 && (
                  <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.body, color:T.driftwood, margin:'4px 0' }}>No upcoming sessions yet — check back soon.</p>
                )}

                {instructor.welcomeMessage && (
                  <div style={{ background:T.ivory, borderRadius:12, border:'1px solid ' + T.border, padding:'14px 16px', marginTop:16 }}>
                    <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.label, color:T.walnut, margin:0, fontStyle:'italic', lineHeight:1.6 }}>"{instructor.welcomeMessage}"</p>
                    <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.caption, color:T.driftwood, margin:'6px 0 0' }}>— {instructor.displayName}</p>
                  </div>
                )}

                <div style={{ marginTop:20 }}>
                  <Btn onClick={() => { setTab('sessions'); window.scrollTo(0, 0); }} style={{ width:'100%', justifyContent:'center' }} disabled={sessions.length === 0}>
                    {sessions.length === 0 ? 'No sessions to book' : 'See sessions & book →'}
                  </Btn>
                </div>

                {!hasOpenSeat && <RequestTimeButton instructor={instructor} setPage={setPage} />}
                <NotifyMeButton instructor={instructor} setPage={setPage} />

                <div style={{ marginTop:16, padding:'12px 0 0', borderTop:'1px solid ' + T.border }}>
                  <p style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:type.caption, color:T.driftwood, margin:0, textAlign:'center' }}>{paysThroughRopida ? PAY_THROUGH_ROPIDA_CARD : 'Pay the instructor directly · Confirmed after payment'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export { MarketplacePage, InstructorProfilePage };
